import { ExprForm } from '@mode-switch/express';
import { Names, Values } from '@mode-switch/model';
import {
  BasicChannel,
  BatchEncoder,
  EncDualAxisXField,
  EncDualAxisYField,
  EncField,
  EncSeries,
  Encoding,
  ExprFrame,
  Guide,
  MarkChannel,
  VariableType,
} from '@mode-vegas/vegas';
import { CompiledField, FormulaOperations } from '@mode/shared/util-switch';
import { VegasFrame } from '../..';

const NAMES_FIELD: EncField = {
  formula: Names.Key,
  variable: { type: VariableType.DISCRETE },
};

const VALUES_FIELD: EncField = {
  formula: Values.Key,
  variable: { type: VariableType.CONTINUOUS },
};

export function addMeasureValuesToSeries(encoding: Encoding, channel: MarkChannel) {
  const guide = new Guide();
  return guide.addEncFieldToSeries(encoding, VALUES_FIELD, channel).encoding;
}

export function addMeasureNamesToBasicChannel(encoding: Encoding, channel: BasicChannel) {
  const guide = new Guide();
  return guide.addEncField(encoding, NAMES_FIELD, channel).encoding;
}

export function clearTextChannel(encoding: Encoding) {
  if (encoding.marks.channel === '*') {
    encoding = removeEncField(encoding, encoding.marks.properties.text.values[0]);
  } else {
    encoding.marks.series.forEach((series) => {
      encoding = removeEncField(encoding, series.properties.text.values[0]);
    });
  }
  return encoding;
}

export function removeEncField(encoding: Encoding, field: EncField | undefined) {
  const guide = new Guide();
  if (field != null && field.id != null) {
    encoding = guide.removeField(encoding, field.id).encoding;
  }
  return encoding;
}

export function ensureMeasureNamesOnColorEncoding(encoding: Encoding, fieldId: string, channel: BasicChannel) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);

  return ensureMeasureNamesWithDualAxis(encoder, fieldId, channel).encoding;
}

export function ensureMeasureNamesWithDualAxis(encoder: BatchEncoder, fieldId: string, channel: BasicChannel) {
  // If possible, Measure Names should be added to the color channel so that each series
  // gets its own color. We will add Measure Names to color if:
  //  - There is no color channel defined for any layer. Add MN to 'All layers'.
  //  - There is no color channel defined for both of the layers associated with the
  //    dual fields.  Add MN to both of the dual field layers.
  //
  const guide = new Guide();

  let result = encoder.complete();
  const summary = result.summary;
  if (summary.color.length === 0) {
    if (Object.prototype.hasOwnProperty.call(summary.colorIds, 'multiple')) {
      // Color is present either only on some layers or is different across layers.
      // Check if the layers associated with the dual axis have a color channel.
      const dualField = encoder.getFieldById(fieldId);
      const pairFieldId = channel === 'row' ? (dualField as EncDualAxisYField).y : (dualField as EncDualAxisXField).x;
      // Get the series associated with the dual axis pair
      const dualPairSeries = result.encoding.marks.series.filter(
        (series: EncSeries) => series.field === dualField.id || series.field === pairFieldId
      );
      const hasColorValues = dualPairSeries.some((series: EncSeries) => series.properties.color.values.length > 0);
      if (!hasColorValues) {
        // Add measure values to each of the dual axis series
        const encoder = guide.newBatchOperation(result.encoding);
        dualPairSeries.forEach((series: EncSeries) => {
          encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color', series.field);
        });
        result = encoder.complete();
      }
    } else {
      // No color assignment at all. Add to 'all' series
      const encoder = guide.newBatchOperation(result.encoding);
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      result = encoder.complete();
    }
  }
  return result;
}

export function makePctOfTotalField(
  compiledField: CompiledField,
  addressingField: EncField | undefined,
  isHorizontalChart: boolean
) {
  let formula: string;
  let frame: ExprFrame | undefined;

  const addrIsSystemField = addressingField?.formula === Names.Key || addressingField?.formula === Values.Key;
  if (addressingField != null && !addrIsSystemField) {
    formula = FormulaOperations.pctOfTotal(compiledField, undefined, ExprForm.TotalUsing.Sum);
    frame = VegasFrame.makeExprFrame(VegasFrame.Type.Custom, [addressingField.formula]);
  } else {
    formula = FormulaOperations.pctOfTotal(compiledField);
    frame = isHorizontalChart
      ? VegasFrame.makeExprFrame(VegasFrame.Type.TableAcross)
      : VegasFrame.makeExprFrame(VegasFrame.Type.TableDown);
  }

  return {
    ...compiledField.toEncField(),
    formula,
    frame,
  };
}
